import {
  collection,
  type DocumentChange,
  onSnapshot,
  query,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { logger } from '@lib/core/env.ts';
import { getFirebaseFirestore } from '@lib/config/firebase.ts';

export interface TrainingNotification {
  id: string;
  batchId: number;
  delete: boolean;
  errorMessage: string | null;
  status: 'trained' | 'error' | 'failed' | string;
  type: string;
  userId: string;
  timestamp: number;
}

export type TrainingStatusUpdate = 'start' | 'update' | 'complete' | 'error';

export interface TrainingNotificationServiceConfig {
  userId: string;
  onTrainingStatusUpdate: (
    status: TrainingStatusUpdate,
    notification: TrainingNotification
  ) => void;
  onError?: (error: Error) => void;
}

export class TrainingNotificationService {
  private unsubscribe: Unsubscribe | null = null;
  private userId: string;
  private onTrainingStatusUpdate: (
    status: TrainingStatusUpdate,
    notification: TrainingNotification
  ) => void;
  private onError?: (error: Error) => void;
  private startTimestamp: number;

  constructor(config: TrainingNotificationServiceConfig) {
    this.userId = config.userId;
    this.onTrainingStatusUpdate = config.onTrainingStatusUpdate;
    this.onError = config.onError;
    this.startTimestamp = Date.now() - 1000 * 3; // 3 seconds ago

    logger.debug('TrainingNotificationService initialized', {
      userId: this.userId,
      startTimestamp: this.startTimestamp,
    });
  }

  startListening(): void {
    if (this.unsubscribe) {
      logger.debug('Training notification listener already active');
      return;
    }

    try {
      const firestore = getFirebaseFirestore();
      const notificationCollection = collection(
        firestore,
        `users/${this.userId}/trainingNotifications`
      );

      const q = query(notificationCollection);

      logger.info('Starting training notification listener', {
        userId: this.userId,
        collection: `users/${this.userId}/trainingNotifications`,
        startTimestamp: this.startTimestamp,
      });

      this.unsubscribe = onSnapshot(q, this.handleSnapshot.bind(this), this.handleError.bind(this));
    } catch (error) {
      const errorInstance = new Error(
        `Failed to start training notification listener: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      logger.error('Training notification listener setup failed', error);

      if (this.onError) {
        this.onError(errorInstance);
      }
    }
  }

  stopListening(): void {
    if (this.unsubscribe) {
      logger.debug('Stopping training notification listener');
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  isListening(): boolean {
    return this.unsubscribe !== null;
  }

  private handleSnapshot(snapshot: QuerySnapshot): void {
    logger.debug('Received training notification snapshot', {
      size: snapshot.size,
      changes: snapshot.docChanges().length,
    });

    snapshot.docChanges().forEach((change: DocumentChange) => {
      this.handleDocumentChange(change);
    });
  }

  /**
   * Handle individual document changes
   */
  private handleDocumentChange(change: DocumentChange): void {
    const doc = change.doc;
    const docTimestamp = Number(doc.id);
    const docData = doc.data();

    logger.debug('Processing document change', {
      type: change.type,
      docId: doc.id,
      docTimestamp,
      startTimestamp: this.startTimestamp,
      isAfterStart: docTimestamp >= this.startTimestamp,
      delete: docData.delete,
    });

    // Only process added documents that are newer than our start timestamp
    // and are not marked for deletion
    if (
      change.type === 'added' &&
      docTimestamp >= this.startTimestamp &&
      docData.delete === false
    ) {
      try {
        const notification: TrainingNotification = {
          id: doc.id,
          batchId: docData.batchId,
          delete: docData.delete,
          errorMessage: docData.errorMessage || null,
          status: docData.status || 'trained',
          type: docData.type || 'unknown',
          userId: this.userId,
          timestamp: docTimestamp,
        };

        logger.info('New training notification received', {
          notificationId: notification.id,
          batchId: notification.batchId,
          type: notification.type,
          status: notification.status,
          hasError: !!notification.errorMessage,
        });

        // Determine the status update type based on notification content
        const statusUpdate = this.determineStatusUpdate(notification);

        // Trigger the callback
        this.onTrainingStatusUpdate(statusUpdate, notification);
      } catch (error) {
        logger.error('Failed to process training notification', {
          docId: doc.id,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }
  }

  /**
   * Determine the training status update type from notification
   */
  private determineStatusUpdate(notification: TrainingNotification): TrainingStatusUpdate {
    logger.debug('Determining status update', {
      notificationId: notification.id,
      batchId: notification.batchId,
      type: notification.type,
      status: notification.status,
      hasErrorMessage: !!notification.errorMessage,
      errorMessage: notification.errorMessage,
    });

    if (notification.errorMessage) {
      logger.info('Status determined as ERROR due to errorMessage', {
        notificationId: notification.id,
        errorMessage: notification.errorMessage,
      });
      return 'error';
    }

    // Check notification type for specific status indicators
    if (notification.type) {
      const typeLC = notification.type.toLowerCase();

      // Check for error
      if (typeLC.includes('error') || typeLC.includes('fail') || typeLC.includes('failed')) {
        logger.info('Status determined as ERROR due to type keywords', {
          notificationId: notification.id,
          type: notification.type,
        });
        return 'error';
      }

      if (typeLC.includes('start') || typeLC.includes('begin')) {
        logger.info('Status determined as START', {
          notificationId: notification.id,
          type: notification.type,
        });
        return 'start';
      }

      if (typeLC.includes('progress') || typeLC.includes('update')) {
        logger.info('Status determined as UPDATE', {
          notificationId: notification.id,
          type: notification.type,
        });
        return 'update';
      }

      if (typeLC.includes('complete') || typeLC.includes('finish') || typeLC.includes('done')) {
        logger.info('Status determined as COMPLETE due to type keywords', {
          notificationId: notification.id,
          type: notification.type,
        });
        return 'complete';
      }
    }

    // Check error status
    if (notification.status) {
      const statusLC = notification.status.toLowerCase();
      if (statusLC.includes('error') || statusLC.includes('fail') || statusLC === 'failed') {
        logger.info('Status determined as ERROR due to status field', {
          notificationId: notification.id,
          status: notification.status,
        });
        return 'error';
      }
    }

    // Since status is always 'trained', treat it as complete
    if (notification.status === 'trained') {
      logger.info('Status determined as COMPLETE due to trained status', {
        notificationId: notification.id,
        status: notification.status,
      });
      return 'complete';
    }

    // Default to update for any other case
    logger.warn('Status determination fell through to default UPDATE', {
      notificationId: notification.id,
      type: notification.type,
      status: notification.status,
    });
    return 'update';
  }

  /**
   * Handle Firestore errors
   */
  private handleError(error: any): void {
    const errorInstance = new Error(
      `Training notification listener error: ${error?.message || 'Unknown error'}`
    );

    logger.error('Training notification listener error', {
      error: error?.message || 'Unknown error',
      code: error?.code,
      userId: this.userId,
    });

    if (this.onError) {
      this.onError(errorInstance);
    }
  }
}

/**
 * Factory function to create a new training notification service
 */
export const createTrainingNotificationService = (
  config: TrainingNotificationServiceConfig
): TrainingNotificationService => {
  return new TrainingNotificationService(config);
};

/**
 * Default training status update handler
 */
export const defaultTrainingStatusHandler = (
  status: TrainingStatusUpdate,
  notification: TrainingNotification
): void => {
  logger.info('Training status update', {
    status,
    notificationId: notification.id,
    batchId: notification.batchId,
    type: notification.type,
    hasError: !!notification.errorMessage,
  });
};
